import { z } from 'zod';
import { Action, Toggles } from '../permissions.js';
import { Email, Id, Password, Role, ShortText } from './common.js';
import { BrandingOutput } from './garage-settings.js';
import { ModuleKey } from './modules.js';
import { ActiveSupportAccess, GarageSlug, GarageStatus, SupportScope } from './platform.js';

export const LoginInput = z.strictObject({ email: Email, password: z.string().min(1).max(200) });

export const MembershipSummary = z.object({
  membershipId: Id,
  garageName: z.string(),
  /** Vague 5 : adresse `/g/<slug>/…` du garage (navigation seulement, aucun droit). */
  garageSlug: GarageSlug,
  /** Vague 5 : un garage suspendu reste listé (message clair) mais ne peut pas devenir actif. */
  status: GarageStatus,
  role: Role,
});

export const ActiveMembership = z.object({
  membershipId: Id,
  garageName: z.string(),
  role: Role,
  toggles: Toggles,
  permissions: z.array(Action),
  branding: BrandingOutput,
  /** Vague 5. */
  garageSlug: GarageSlug,
  /** Vague 5 : accès support en cours dans ce garage (bandeau pour toute l'équipe), sinon `null`. */
  supportAccess: ActiveSupportAccess.nullable(),
  /**
   * Vague 7 : modules actifs du garage (`resolveEnabledModules`) : l'interface filtre menu, widgets, onglets
   * et actions. Obligatoire depuis l'intégration de la vague 7 (02/10/2026).
   */
  enabledModules: z.array(ModuleKey),
});

/**
 * Vague 5 : session support d'un administrateur plateforme entré dans un garage avec un accès accordé.
 * Revérifiée à chaque requête par le cœur ; `permissions` = `supportPermissions(scope, amountsVisible)`.
 */
export const SupportSession = z.object({
  accessId: Id,
  garageSlug: GarageSlug,
  garageName: z.string(),
  scope: SupportScope,
  amountsVisible: z.boolean(),
  grantedUntil: z.iso.datetime({ offset: true }),
  permissions: z.array(Action),
  branding: BrandingOutput,
  /**
   * Intégration de la vague 7 (02/10/2026) : modules actifs du garage visité (`resolveEnabledModules`),
   * comme `ActiveMembership.enabledModules` : le menu du support est celui du garage.
   */
  enabledModules: z.array(ModuleKey),
});

/** Vague 5 : fonctions disponibles sur ce serveur (configuration), pour masquer ce qui ne l'est pas. */
export const Features = z.object({
  /** Lecture de plaque disponible (modèle présent) : sinon le bouton « Scanner la plaque » est masqué. */
  plateScan: z.boolean(),
  /** Notifications push : clé publique VAPID, ou `null` si le push n'est pas configuré. */
  pushPublicKey: z.string().nullable(),
});
export const MeOutput = z.object({
  user: z.object({ id: Id, name: z.string(), email: z.string() }),
  /** Garage actif de la session ; `null` si l'utilisateur doit en choisir un. */
  active: ActiveMembership.nullable(),
  memberships: z.array(MembershipSummary),
  /** Vague 5 : administrateur plateforme (accès à `/admin`, jamais au contenu des garages sans accès support). */
  isPlatformAdmin: z.boolean(),
  /** Vague 5 : session support en cours, sinon `null`. Prioritaire sur `active` dans l'espace garage. */
  support: SupportSession.nullable(),
  features: Features,
  /**
   * Vague 7 : session de démonstration (`demo.enter`) : bandeau « Démonstration · données fictives ·
   * réinitialisée chaque nuit ». Obligatoire depuis l'intégration de la vague 7.
   */
  demo: z.boolean(),
  /** Vague 7 : garage de démonstration réinitialisé chaque nuit (texte du bandeau), `null` hors démonstration. */
  demoResetNightly: z.boolean().nullable(),
});
export type MeOutput = z.infer<typeof MeOutput>;

/**
 * Changement de garage (vague 5) : par **slug**, résolu uniquement parmi les appartenances actives de
 * l'utilisateur (slug inconnu, d'un autre garage ou suspendu : même 404, aucune énumération).
 * `membershipId` reste accepté (compatibilité) ; jamais d'identifiant de garage.
 * Quitte une éventuelle session support (l'accès lui-même reste accordé jusqu'à son échéance).
 */
export const SwitchGarageInput = z.union([
  z.strictObject({ slug: GarageSlug }),
  z.strictObject({ membershipId: Id }),
]);

export const PasswordResetRequestInput = z.strictObject({ email: Email });
export const PasswordResetConfirmInput = z.strictObject({ token: z.string().min(20).max(200), password: Password });

export const InvitationTokenInput = z.strictObject({ token: z.string().min(20).max(200) });
export const InvitationInspectOutput = z.object({
  garageName: z.string(),
  email: z.string(),
  role: Role,
  /** Vague 5 : nom de l'invité saisi à l'invitation (création d'un garage par la plateforme), à préremplir. */
  inviteeName: z.string().nullable(),
  /** Un compte existe déjà pour cet e-mail : l'invité confirme avec son mot de passe. */
  accountExists: z.boolean(),
});
export const InvitationAcceptInput = z.strictObject({
  token: z.string().min(20).max(200),
  name: ShortText.optional(),
  password: Password,
});
